import { AggregatedArticle } from "./useAggregatedArticles";
import { useMemoryFilteredData } from "@coworker/apprestructured/src/shared/filters/utils/apply/useMemoryFilteredData";
import { useHfbMultiSelectArticleFilter } from "../filters/useHfbMultiSelectArticleFilter";
import { useAlertSelectArticleFilter } from "../filters/useAlertSelectArticleFilter";
import { useEdsDatesSelectArticleFilter } from "../filters/useEdsDatesSelectArticleFilter";
import { useTasksSelectArticleFilter } from "../filters/useTasksSelectArticleFilter";
import { useGroupedArticlesSelectArticleFilter } from "../filters/useGroupedArticlesSelectArticleFilter";
import { useSortSelectArticleFilter } from "../filters/useSortSelectArticleFilter";
import preferences from "@coworker/enums/profilePreferences";
import { useUserPreferenceStoreFilterValues } from "@coworker/apprestructured/src/shared/filters/utils/store/useUserPreferenceStoreFilterValues";
import { useEdsDates } from "./useEdsDates";
import {
  Filter,
  FilterValue,
  InputFilter,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { useHfbDepartments } from "./useHfbDepartments";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";

interface DataFilter {
  filter: Filter;
}

function useBaseMediaDataFilters(
  articles: AggregatedArticle[] | undefined,
  values: FilterValue[]
) {
  const hfbDepartments = useHfbDepartments(articles ?? []);
  const edsDates = useEdsDates(articles ?? []);

  return {
    hfbFilter: useHfbMultiSelectArticleFilter(hfbDepartments),
    alertFilter: useAlertSelectArticleFilter(),
    edsFilter: useEdsDatesSelectArticleFilter(values, edsDates),
    tasksFilter: useTasksSelectArticleFilter(),
    groupedFilter: useGroupedArticlesSelectArticleFilter(),
    sortFilter: useSortSelectArticleFilter(values),
  };
}

function useBaseMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined,
  buildDataFilters: (
    allFilters: ReturnType<typeof useBaseMediaDataFilters>
  ) => ApplyFilter<InputFilter, AggregatedArticle>[]
) {
  const { filterValues, setFilterValues } = useUserPreferenceStoreFilterValues(
    preferences.MEDIA_FILTERS_ARTICLES
  );

  const allFiltersForStore = useBaseMediaDataFilters(articles, filterValues);
  const dataFilters = buildDataFilters(allFiltersForStore);

  const useFiltersFactory = (values: FilterValue[]) => {
    const allFiltersForFactory = useBaseMediaDataFilters(articles, values);
    const factoryDataFilters = buildDataFilters(allFiltersForFactory);
    return factoryDataFilters.map(({ filter }) => filter);
  };

  function useFilteredArticleData(articles: AggregatedArticle[] | undefined) {
    return useMemoryFilteredData<AggregatedArticle>(
      articles,
      dataFilters,
      filterValues
    );
  }

  return {
    useFiltersFactory,
    filterValues,
    setFilterValues,
    useFilteredArticleData,
  };
}

// This code will probably go into AggregatedArticleListing when old filter solution is removed
export function useMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined
) {
  return useBaseMediaArticlesFilters(articles, (filters) => [
    filters.hfbFilter,
    filters.alertFilter,
    filters.edsFilter,
    filters.tasksFilter,
    filters.groupedFilter,
    filters.sortFilter,
  ]);
}

export function useGroupMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined
) {
  return useBaseMediaArticlesFilters(articles, (filters) => [
    filters.hfbFilter,
    filters.alertFilter,
    filters.edsFilter,
    filters.tasksFilter,
    filters.sortFilter,
  ]);
}
